/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import moment from 'moment';

import type { TranslatorProps } from 'react-i18next';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore';

import Loading from 'components/Loading/Loading';

import Form from 'forms/_.extend';

import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import ScheduleCalibrationForm from './ScheduleCalibrationForm/ScheduleCalibrationForm';
import ScheduleCalibrationSuccess from './ScheduleCalibrationSuccess/ScheduleCalibrationSuccess';
import ScheduleCalibrationError from './ScheduleCalibrationError/ScheduleCalibrationError';

import styles from '../../../ActionDrawer.module.scss';
import { InputValues } from '../ReportAssetFault/ReportAssetFaultForm/ReportAssetFaultForm';
import { isAdmin } from '../../.././../../permissions/general.js';

type TypeProps = {
  t: TranslatorProps,
  actionDrawerStore: TypeActionDrawerStore,
  assetStore: Object,
};

@translate()
@inject('actionDrawerStore', 'assetStore')
@observer
export default class ScheduleAssetCalibration extends React.Component<*> {
  createForm = () => {
    const { t, actionDrawerStore, assetStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    const fields: Array<Object> = [
      {
        name: 'collectionDetails',
        label: t('general:labels.collection_details'),
        fields: [
          {
            name: 'collectionDate',
            label: t('general:assets.labels.date_available_for_collection'),
            rules: `required|date|after:${moment(new Date()).format('MMMM DD YYYY')}`,
            value: new Date(
              moment()
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
          {
            name: 'newLocation',
            rules: 'string',
            placeholder: t('general:new_location'),
            value: '',
          },
          {
            name: 'newContactDetailsName',
            rules: 'string',
            placeholder: t('general:name'),
            value: '',
          },
          {
            name: 'newContactDetailsEmail',
            rules: 'string',
            placeholder: t('general:email'),
            value: '',
          },
        ],
      },
      {
        name: 'additionalInformation',
        label: t('general:labels.additional_info'),
        fields: [
          {
            name: 'replacementRequired',
            label: t('general:assets.reasons.replacement_required'),
            rules: 'required|boolean',
            value: false,
          },
          {
            name: 'keepOriginalPackaging',
            label: t('general:assets.reasons.original_packaging') + '?',
            rules: 'required|boolean',
            value: false,
            related: ['additionalInformation.packagingDescription'],
          },
          {
            name: 'packagingDescription',
            label: t('general:labels.describe_packaging_details'),
            rules: 'required_if:additionalInformation.keepOriginalPackaging,false|string',
            value: '',
          },
        ],
      },
    ];

    if (!asset.owned) {
      fields[0].fields.push(
        {
          name: 'collectLocation',
          label: t('general:assets.labels.collect'),
          rules: 'required|string',
          value: 'CURRENT_LOCATION',
          options: [
            {
              id: 'CURRENT_LOCATION',
              label: t('general:labels.from_current_location'),
              value: 'CURRENT_LOCATION',
            },
            {
              id: 'CONTRACT_LOCATION',
              label: t('general:contracts.labels.from_delivery_location'),
              value: 'CONTRACT_LOCATION',
            },
            {
              id: 'NEW_LOCATION',
              label: t('general:assets.labels.collect_new'),
              value: 'NEW_LOCATION',
            },
          ],
        },
        {
          name: 'contactDetails',
          label: t('general:labels.contact_details'),
          rules: 'required|string',
          value: 'USER_CONTACT_DETAILS',
          options: [
            {
              id: 'USER_CONTACT_DETAILS',
              label: t('general:labels.use_my_contact_details'),
              value: 'USER_CONTACT_DETAILS',
            },
            {
              id: 'CONTRACT_PICKUP_DETAILS',
              label: t('general:contracts.labels.use_delivery_details'),
              value: 'CONTRACT_PICKUP_DETAILS',
            },
            {
              id: 'NEW_CONTACT_DETAILS',
              label: t('general:assets.labels.new_contact_details'),
              value: 'NEW_CONTACT_DETAILS',
            },
          ],
        }
      );
    }

    const hooks: Object = {
      onInit(data: Object) {
        if (!(asset && asset.account)) {
          assetStore.clearDetails();
          assetStore.loadAsset(asset.id);
        }
      },
      onSuccess(data: Object) {
        const {
          collectLocation,
          contactDetails,
          collectionDate,
          newLocation,
          newContactDetailsName,
          newContactDetailsEmail,
        }: Object = data.values().collectionDetails;

        const newDeliveryDetails =
          collectLocation === InputValues.NEW_LOCATION ||
          contactDetails === InputValues.NEW_CONTACT_DETAILS
            ? {
                location: newLocation,
                contact: {
                  name: newContactDetailsName,
                  email: newContactDetailsEmail,
                },
              }
            : null;

        const {
          replacementRequired,
          keepOriginalPackaging,
          packagingDescription,
        }: Object = data.values().additionalInformation;

        actionDrawerStore.scheduleAssetCalibration(asset.id, {
          replacement_required: replacementRequired,
          keep_original_packaging: keepOriginalPackaging,
          collect_location: collectLocation,
          contact_details: contactDetails,
          packaging_description: packagingDescription,
          collection_date: moment(collectionDate).format('YYYY-MM-DD'),
          new_delivery_details: JSON.stringify(newDeliveryDetails),
        });
      },
    };
    return new Form(
      { fields },
      {
        hooks,
        name: 'scheduleAssetCalibration',
      }
    );
  };

  render(): React.Element<*> {
    const { t, actionDrawerStore }: TypeProps = this.props;

    const asset: Object = this.props.formData;

    const title: string = isAdmin()
      ? t('general:calibrations.schedule_calibration')
      : t('general:calibrations.schedule_calibration_request');

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <ScheduleCalibrationError asset={asset} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <ScheduleCalibrationSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <ScheduleCalibrationForm asset={asset} form={this.createForm()} />
      </div>
    );
  }
}
